import { DataTableSearch } from '@/components/ui/table/data-table-search';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { downloadCSV } from '@/lib/download-file';
import { toast } from 'sonner';
import { AlertModal } from '@/components/modal/alert-modal';
import { memo, useState } from 'react';
import { useQueryState } from 'nuqs';
import { searchParams } from '@/lib/searchparams';
import type { Organisation } from '@/services/types';
import { useAuthStore } from '@/store/auth';
import { useDeleteOrganisationsMutation } from '@/services/organisations/queries';

type OrgTableActionProps = {
  data: Organisation[];
  checkedRows: string[];
  setCheckedRows: (rows: string[]) => void;
};

function OrgTableAction({ data, checkedRows, setCheckedRows }: OrgTableActionProps) {
  const [searchQuery, setSearchQueryState] = useQueryState('q', searchParams.q);
  const [currentSort, setCurrentSort] = useQueryState('sort', searchParams.sort);
  const [verifiedFilter, setVerifiedFilter] = useQueryState('verified', searchParams.verified);
  const [, setPageState] = useQueryState('page', searchParams.page);
  const { context } = useAuthStore();
  const hasActiveFilters = currentSort || searchQuery || verifiedFilter;

  const setSearchQuery = setSearchQueryState as any;
  const setPage = setPageState as any;
  const [open, setOpen] = useState(false);

  const { mutate: deleteOrganisations, isPending } = useDeleteOrganisationsMutation(
    () => {
      toast.success('Organisations deleted successfully');
      setCheckedRows([]);
      setOpen(false);
    },
    (error) => {
      toast.error(error.response.data.message || 'Failed to delete organisations');
      setOpen(false);
    }
  );

  const handleDownload = async () => {
    const toISO = (d?: Date) => (d ? new Date(d).toISOString() : '');
    const toYear = (d?: Date) => (d ? new Date(d).getFullYear() : '');

    const mapped = (data || []).map((org) => ({
      name: org.name,
      email: org.email || '',
      phoneNumber: org.phoneNumber || '',
      address: org.address || '',
      website: org.website || '',
      linkedInUrl: org.linkedInUrl || '',
      headOfficeLocation: org.headOfficeLocation || '',
      officeLocations: org.officeLocations?.join(' | ') || '',
      paidStaffLocations: org.paidStaffLocations?.join(' | ') || '',
      bio: org.bio || '',
      mission: org.mission || '',
      yearFounded: toYear(org.yearFounded),
      verified: org.verified ? 'Yes' : 'No',
      contactPerson_name: org.contactPerson?.name || '',
      contactPerson_email: org.contactPerson?.email || '',
      contactPerson_bio: org.contactPerson?.bio || '',
      contactPerson_linkedInUrl: org.contactPerson?.linkedInUrl || '',
      createdAt: toISO(org.createdAt),
      updatedAt: toISO(org.updatedAt)
    }));

    downloadCSV(mapped, 'organisations');
  };

  const sortingOptions = [
    { label: 'Most Recent', value: 'dsc' },
    { label: 'Least Recent', value: 'asc' }
  ];

  const verificationOptions = [
    { label: 'All Organizations', value: 'all' },
    { label: 'Verified', value: 'true' },
    { label: 'Not Verified', value: 'false' }
  ];

  const handleSortingChange = (value: string) => {
    setCurrentSort(value);
  };

  const handleVerificationChange = (value: string) => {
    setVerifiedFilter(value === 'all' ? null : value);
  };

  const handleDelete = async () => {
    if (!checkedRows || checkedRows.length === 0) {
      toast.warning('No organisations selected for deletion');
      return;
    }
    deleteOrganisations({ organisationIds: checkedRows });
  };

  return (
    <div className='flex flex-wrap items-center justify-between gap-4 px-5'>
      <AlertModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onConfirm={handleDelete}
        loading={isPending}
        title='Delete selected organisations'
        description={`Are you sure you want to delete ${checkedRows?.length || 0} selected organisations?`}
      />
      
      <DataTableSearch
        searchKey='organisations'
        searchQuery={searchQuery || ''}
        setSearchQuery={setSearchQuery}
        setPage={setPage}
        className='bg-[#F7F7F7]'
      />

      <div className='flex items-center gap-4'>
        {/* Verification Filter - Only for workspace users */}
        {context === 'workspace' && (
          <div className='flex items-center gap-2'>
            <p className='font-grotesk text-text-primary'>Status</p>
            <Select onValueChange={handleVerificationChange} value={verifiedFilter || 'all'}>
              <SelectTrigger className='w-[160px]'>
                <SelectValue placeholder='All Organizations' />
              </SelectTrigger>
              <SelectContent>
                {verificationOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Sorting Select */}
        <div className='flex items-center gap-2'>
          <p className='font-grotesk text-text-primary'>Sort By</p>
          <Select onValueChange={handleSortingChange} value={currentSort || ''}>
            <SelectTrigger className='w-[150px]'>
              <SelectValue placeholder='Select sorting' />
            </SelectTrigger>
            <SelectContent>
              {sortingOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Export Button */}
        <div className='flex items-center gap-2'>
          <Button
            variant={'outline'}
            onClick={handleDownload}
          >
            Export Organisations
          </Button>
        </div>

        {/* Reset Filters Button */}
        {hasActiveFilters && (
          <Button
            variant='outline'
            onClick={() => {
              setSearchQuery(null);
              setCurrentSort(null);
              setVerifiedFilter(null);
            }}
          >
            Reset Filters
          </Button>
        )}

        {/* Bulk Delete Button */}
        {checkedRows?.length > 0 && (
          <Button
            className='bg-red-800 transition-all hover:bg-red-900'
            onClick={() => setOpen(true)}
          >
            Delete organisations ({checkedRows.length})
          </Button>
        )}
      </div>
    </div>
  );
}

export default memo(OrgTableAction);
